import type { IStudyFilterFragmentViewModel } from '../../ViewModel/Abstraction/IStudyFilterFragmentViewModel.csharp';
import type { FacetStudyFilterFragmentViewModel } from '../../ViewModel/StudyFilterFragments/FacetStudyFilterFragmentViewModel.csharp';
import { StudyFilterFragmentType } from '../../ViewModel/StudyFilterFragments/StudyFilterFragmentType.csharp';
import type { TextStudyFilterFragmentViewModel } from '../../ViewModel/StudyFilterFragments/TextStudyFilterFragmentViewModel.csharp';
import type { SelectedOptionsMap } from '../Components/InteractiveFilters/InteractiveFilters';
import type {
    ExtendedDropdownElement,
    StudyFilterSectionElement,
} from '../Components/StudyFilterSection/StudyFilterSection';
import type { ResultsSortOrder } from '~/Blocks/StudyFilterSectionBlock/View/Components/StudyFilterSection/ResultsSortOrder';
import ExecutionContext from '~/Features/ExecutionContext';
import type { QueryParameter } from '~/Partials/SearchBase/QueryParameterUtils';
import { getQueryParametersFromUrl } from '~/Partials/SearchBase/QueryParameterUtils';
import { isTruthy } from '~/Partials/Utils/utils';

const isTextFragment = (fragment: IStudyFilterFragmentViewModel): fragment is TextStudyFilterFragmentViewModel =>
    fragment.type === StudyFilterFragmentType.Text;

const isFacetFragment = (fragment: IStudyFilterFragmentViewModel): fragment is FacetStudyFilterFragmentViewModel =>
    fragment.type === StudyFilterFragmentType.Facet;

export const mapFragments = (fragments: IStudyFilterFragmentViewModel[]): StudyFilterSectionElement[] =>
    fragments.map(fragment => {
        if (isTextFragment(fragment)) {
            return fragment.value;
        }

        if (isFacetFragment(fragment)) {
            const element: ExtendedDropdownElement = {
                id: fragment.facet.id,
                placeholder: fragment.placeholder,
                selectLabel: fragment.label,
                items: fragment.facet.options,
                initialSelections: fragment.initialSelections,
                areOptionsLimited: fragment.areOptionsLimited,
            };

            return element;
        }

        throw new Error(`Fragment type ${fragment.type} is not supported`);
    });

export const isDropdownElement = (element: StudyFilterSectionElement): element is ExtendedDropdownElement =>
    typeof element !== 'string';

export type DropdownElementsMap = Record<string, ExtendedDropdownElement>;

export const groupDropdownElementsById = (elements: StudyFilterSectionElement[]): DropdownElementsMap =>
    elements.filter(isDropdownElement).reduce((map, element) => {
        map[element.id] = element;

        return map;
    }, {} as DropdownElementsMap);

export const getInitiallySelectedOptions = (elements: StudyFilterSectionElement[]): SelectedOptionsMap =>
    elements
        .filter(isDropdownElement)
        .map(element => {
            const selectedItemsInOrder = element.initialSelections
                .map(selectedId => element.items.find(item => item.id === selectedId))
                .filter(isTruthy);

            return [element.id, selectedItemsInOrder] as const;
        })
        .reduce((map, [facetId, initialValues]) => {
            map[facetId] = initialValues;

            return map;
        }, {} as SelectedOptionsMap);

export const isAnyFilterSelected = (selectedOptions: SelectedOptionsMap) =>
    Object.values(selectedOptions).some(option => option !== null);

export const buildSearchParams = (
    selectedOptions: SelectedOptionsMap,
    dropdownElementsMap: DropdownElementsMap,
    sortOrder: ResultsSortOrder,
    forDisplayOnly = false
): QueryParameter[] => {
    const params = Object.entries(selectedOptions).reduce((queryParameters, item) => {
        const [id, options] = item;

        if (options.length > 0) {
            return queryParameters.concat(options.map(option => ({ name: id, value: option.id })));
        }

        if (!forDisplayOnly && dropdownElementsMap[id].areOptionsLimited) {
            const limitedOptions = dropdownElementsMap[id].items
                .filter(opt => opt.id)
                .map(opt => ({
                    name: id,
                    value: opt.id,
                }));

            return queryParameters.concat(limitedOptions);
        }

        return queryParameters;
    }, [] as QueryParameter[]);

    return forDisplayOnly ? params : params.concat({ name: 'sort', value: sortOrder });
};

export const getExternalQueryParametersOnFirstRender = (dropdownElementsMap: DropdownElementsMap) => {
    if (ExecutionContext.IsServerSide()) {
        return [];
    }

    const initialQueryParameters = getQueryParametersFromUrl(window.location.search);
    const dropdownElementsQueryParameters = Object.keys(dropdownElementsMap);

    return initialQueryParameters.filter(param => !dropdownElementsQueryParameters.includes(param.name));
};
